import { ArrowLeft } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-router';

import { ErrorState } from '../components/ErrorState.js';

/**
 * Plan F8. Three small pages, kept together because none is more than a card.
 *
 * All three render outside the shell, so they supply the canvas themselves:
 * `bg-surface-alt` behind a single bright `ErrorState` card, which is the same
 * figure/ground rule the shell applies — the container holding the information
 * is the bright plane, and everything around it recedes. A dead end is still a
 * screen of the product, and it should look like one.
 */

/** The one shape all three take, so a 404 and a dropped connection do not land differently. */
function StatusScreen({ children }: { children: ReactElement }): ReactElement {
  return (
    <main className="flex min-h-screen items-center justify-center bg-surface-alt px-[var(--space-6)] py-[var(--space-10)]">
      <div className="w-full max-w-lg">{children}</div>
    </main>
  );
}

export function NotFoundPage(): ReactElement {
  return (
    <StatusScreen>
      <ErrorState
        title="Page not found"
        body="That page does not exist, or it has moved."
        action={
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-[var(--space-2)] text-sm text-primary underline underline-offset-2"
          >
            <ArrowLeft aria-hidden="true" size={14} strokeWidth={2} />
            Back to the overview
          </Link>
        }
      />
    </StatusScreen>
  );
}

export function ForbiddenPage(): ReactElement {
  return (
    <StatusScreen>
      <ErrorState
        title="You do not have access to this"
        body="Your administrator can grant it if you need it."
      />
    </StatusScreen>
  );
}

export function OfflinePage(): ReactElement {
  return (
    <StatusScreen>
      <ErrorState
        title="No connection to the panel"
        body="Check your internet connection. Nothing you did was lost."
        onRetry={() => window.location.reload()}
      />
    </StatusScreen>
  );
}

/**
 * A placeholder for the routes Phases 05–07 fill in. It exists so the nav can
 * be complete and permission-gated now: a link that 404s teaches the user the
 * nav is unreliable, while "coming soon" is at least true.
 *
 * Unlike the three above it renders **inside** the shell, which already draws
 * the canvas — so it stays a bare card and adds no second one.
 */
export function ComingSoonPage({ title }: { title: string }): ReactElement {
  return (
    <ErrorState title={title} body="This screen is not built yet. It lands in a later release." />
  );
}
